import React, { useEffect, useRef, useState } from 'react';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  title: React.ReactNode;
  placement?: Placement;
  children: React.ReactNode;
  className?: string;
}

const placementStyles: Record<Placement, { container: React.CSSProperties }> = {
  top: {
    container: {
      bottom: 'calc(100% + 8px)',
      left: '50%',
      transform: 'translateX(-50%)'
    }
  },
  bottom: {
    container: { top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }
  },
  left: {
    container: { right: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)' }
  },
  right: {
    container: { left: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)' }
  }
};

export default function Tooltip({ title, placement = 'top', children, className }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const hideTimeout = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    };
  }, []);

  const show = () => {
    if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    setVisible(true);
  };
  const hide = () => {
    // small delay to allow hover into tooltip content if needed
    hideTimeout.current = window.setTimeout(() => setVisible(false), 100);
  };

  const styles = placementStyles[placement];

  return (
    <span
      className={`inline-block relative ${className || ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={0}
      aria-haspopup="true"
    >
      {children}
      {visible && (
        <div
          role="tooltip"
          style={{
            whiteSpace: 'nowrap',
            position: 'absolute',
            ...styles.container
          }}
          className="bg-gradient-to-br font-gilroy-medium py-[6px] px-[10px] z-[9999] from-gray-100 to-gray-300/50   shadow-md backdrop-blur-sm text-sm border border-gray-200 rounded-lg"
        >
          <div>{title}</div>
        </div>
      )}
    </span>
  );
}
